import authFetch from './authFetch';
import { graphqlAuthFetch } from './graphqlFetch';
import getConfig from 'next/config';

const {
  publicRuntimeConfig: { ACCOUNTS_SERVICE_HOST },
} = getConfig();

const featureFlagGraphQL = false;

async function request(url: string, options?: RequestInit): Promise<any> {
  const response = await authFetch(url, {
    cache: 'default',
    credentials: 'same-origin',
    headers: {
      'Content-Type': 'application/json',
    },
    ...options,
  });

  if (!response.ok) {
    throw await response.json();
  }

  const result = await response.json();
  if (result?.status === false) {
    throw new Error(result.message);
  }

  return result;
}

async function graphqlRequest(options?: RequestInit): Promise<any> {
  const response = await graphqlAuthFetch(`${ACCOUNTS_SERVICE_HOST}/graphql/`, {
    ...options,
  });

  if (!response.ok) throw await response.json();
  if (response.status === 204) return;

  return response.json();
}

async function fetchPlayersAndGroups(): Promise<any> {
  const response = await graphqlRequest({
    body: JSON.stringify({
      query: `{
        players {
          id,
          memberId,
          name,
          dob,
          avatarId,
          grade,
          isGradeLocked,
          isGradeHidden,
          createdAt,
          updatedAt,
          status,
          groups,
        }
        groups {
          id,
          name,
          description,
          memberId,
          avatarId,
          isActive,
          createdAt,
          updatedAt,
          players,
        }
      }`,
    }),
  });

  return response?.data;
}

export enum Grade {
  K = 'kindergarten',
  One = 'first',
  Two = 'second',
  Three = 'third',
  Four = 'fourth',
  Five = 'fifth',
}

export interface Player {
  id?: string;
  memberId?: string;
  name?: string;
  dob?: string;
  gender?: string;
  avatar?: string;
  avatarId?: number;
  grade?: Grade;
  isGradeLocked?: boolean;
  isGradeHidden?: boolean;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  groups?: string[];
}

export interface PlayerGroup {
  id: string;
  name: string;
  description: string;
  memberId: string;
  avatarId: number;
  isActive: boolean;
  order?: number;
  createdAt: string;
  updatedAt?: string;
  players: string[];
}

export interface PlayersMap {
  [id: string]: Player;
}

export interface PlayerGroupsMap {
  [id: string]: PlayerGroup;
}

function playerFromResponse(data: { [key: string]: any }): Player {
  const avatar = data?.avatar ?? data?.childAvatar;

  return {
    id: data?.PlayerId ?? data?.childId,
    memberId: data?.MemberId ?? data?.memberId,
    name: data?.PlayerName ?? data?.childName,
    dob: data?.PlayerDOB,
    avatar: avatar?.internal_name ?? avatar?.internalName,
    grade: data?.PlayerGrade ?? data?.childGrade,
    isGradeLocked: data?.lockedGrade ?? false,
    isGradeHidden: data?.hiddenGrade ?? false,
    createdAt: data?.DateCreated,
    updatedAt: data?.DateUpdated,
    status: data?.status,
    groups: data?.groups,
  };
}

function groupFromResponse(data: { [key: string]: any }): PlayerGroup {
  return {
    id: data?.PlayerGroupId,
    name: data?.GroupName,
    description: data?.GroupDescription,
    memberId: data?.MemberId,
    avatarId: data?.GroupAvatarId,
    isActive: !!parseInt(data?.GroupActive),
    order: data.GroupOrder ? parseInt(data.GroupOrder) : null,
    createdAt: data?.DateCreated,
    updatedAt: data?.DateUpdated,
    players: data?.children ?? [],
  };
}

function convertToYMD(timestamp: number): string {
  const dt = new Date(+timestamp);

  let mm = (dt.getMonth() + 1).toString(); // getMonth() is zero-based
  let dd = dt.getDate().toString();
  let hh = dt.getHours().toString();
  let ii = dt.getMinutes().toString();
  let ss = dt.getSeconds().toString();

  let leading = mm.length === 2 ? '' : '0';
  mm = leading + mm;

  leading = dd.length === 2 ? '' : '0';
  dd = leading + dd;

  leading = hh.length === 2 ? '' : '0';
  hh = leading + hh;

  leading = ii.length === 2 ? '' : '0';
  ii = leading + ii;

  leading = ss.length === 2 ? '' : '0';
  ss = leading + ss;

  return (
    dt.getFullYear() + '-' + mm + '-' + dd + ' ' + hh + ':' + ii + ':' + ss
  );
}

function playerFromGraphQLResponse(data): Player {
  const createdAtMoment = convertToYMD(Number(data.createdAt));
  const updatedAtMoment =
    data.updatedAt === null ? null : convertToYMD(Number(data.updatedAt));

  return {
    id: data.id,
    memberId: data.memberId,
    name: data.name,
    dob: data.dob,
    avatarId: data.avatarId,
    grade: data.grade,
    isGradeLocked: data.isGradeLocked,
    isGradeHidden: data.isGradeHidden,
    createdAt: createdAtMoment,
    updatedAt: updatedAtMoment,
    status: data.status,
    groups: data.groups,
  };
}

function groupFromGraphQLResponse(data): PlayerGroup {
  const createdAtMoment = convertToYMD(Number(data.createdAt));
  const updatedAtMoment =
    data.updatedAt === null ? null : convertToYMD(Number(data.updatedAt));

  return {
    id: data.id,
    name: data.name,
    description: data.description,
    memberId: data.memberId,
    avatarId: data.avatarId,
    isActive: data.isActive,
    createdAt: createdAtMoment,
    updatedAt: updatedAtMoment,
    players: data.players,
  };
}

export async function getAll(): Promise<[PlayersMap, PlayerGroupsMap]> {
  if (featureFlagGraphQL) {
    const { players, groups } = await fetchPlayersAndGroups();
    const playersMap = {};
    const groupsMap = {};

    players.forEach(p => {
      playersMap[p.id] = playerFromGraphQLResponse(p);
    });
    groups.forEach(g => {
      groupsMap[g.id] = groupFromGraphQLResponse(g);
    });

    return [playersMap, groupsMap];
  }

  const { children, groups } = await request('/api/children/childrenGroups/');
  const playersMap = {};
  const groupsMap = {};
  Object.entries(children).forEach(([id, player]) => {
    playersMap[id] = playerFromResponse(player);
  });
  Object.entries(groups).forEach(([id, group]) => {
    groupsMap[id] = groupFromResponse(group);
  });

  return [playersMap, groupsMap];
}

export interface CreatePlayerParams {
  name: string;
  dob?: string;
  gender?: string;
  grade?: string;
  avatar?: string;
}

async function create(params: CreatePlayerParams): Promise<Player> {
  const res = await request('/api/child', {
    method: 'POST',
    body: JSON.stringify({
      childName: params.name,
      childDOB: params?.dob,
      childGender: params?.gender,
      childGrade: params?.grade,
      childAvatar: params?.avatar,
    }),
  });

  return playerFromResponse(res.data);
}

export interface UpdatePlayerParams {
  name?: string;
  grade?: string;
  avatar?: string;
  isGradeLocked?: boolean;
  isGradeHidden?: boolean;
}

async function update(
  childId: string,
  params: UpdatePlayerParams
): Promise<Player> {
  const res = await request(`/api/children/update/session/${childId}`, {
    method: 'POST',
    body: JSON.stringify({
      name: params?.name,
      grade: params?.grade,
      avatar: params?.avatar,
      lockedGrade: params?.isGradeLocked,
      hiddenGrade: params?.isGradeHidden,
    }),
  });

  return playerFromResponse(res);
}

export default {
  getAll,
  create,
  update,
};
